import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { Map, type MapPin } from "@/web/components/styled/Map/Map";
import { geocodeAddress } from "@/web/lib/geocoding";
import type { CanonicalAddress } from "@tycho/web-bindings/types";
import type { ReviewComp } from "./types";

type Coord = { lon: number; lat: number };

async function geocodeOne(address: CanonicalAddress): Promise<Coord | null> {
  const coords = await geocodeAddress(address);
  return coords ? { lon: coords.longitude, lat: coords.latitude } : null;
}

// Stable key for the geocoding query — falling back on individual fields keeps
// the cache warm across `ReviewComp` identity changes (e.g. when the parent
// passes a freshly spread array but with the same underlying addresses).
function addressKey(a: CanonicalAddress): string {
  return `${a.addressLine1}|${a.addressLine2 ?? ""}|${a.city}|${a.state}|${a.zip}|${a.countryCode}`;
}

export function CompMap({
  targetAddress,
  comps,
}: {
  targetAddress: CanonicalAddress;
  comps: ReviewComp[];
}) {
  const queryKey = useMemo(
    () => [
      "mapbox-geocode",
      addressKey(targetAddress),
      ...comps.map((c) => `${addressKey(c.address)}|${c.latitude ?? ""}|${c.longitude ?? ""}`),
    ],
    [targetAddress, comps],
  );

  const { data: points } = useQuery({
    queryKey,
    queryFn: async () => {
      const [target, ...rest] = await Promise.all([
        // TODO: use targetable property lat/long if available
        geocodeOne(targetAddress),
        ...comps.map((c) =>
          c.latitude != null && c.longitude != null
            ? { lon: c.longitude, lat: c.latitude }
            : geocodeOne(c.address),
        ),
      ]);
      const compPoints = rest.map((coord, i) => {
        const comp = comps[i];
        return coord && comp ? { id: comp.id.toString(), coord } : null;
      });
      return { target, comps: compPoints };
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const pins = useMemo<MapPin[]>(() => {
    if (!points) return [];
    const result: MapPin[] = [];
    for (const p of points.comps) {
      if (!p) continue;
      result.push({
        id: p.id,
        latitude: p.coord.lat,
        longitude: p.coord.lon,
        color: "var(--color-withus-blue)",
      });
    }
    if (points.target) {
      result.push({
        id: "target",
        latitude: points.target.lat,
        longitude: points.target.lon,
        color: "var(--color-withus-yellow)",
        centerColor: "var(--color-withus-dark-blue)",
        zIndex: 10,
      });
    }
    return result;
  }, [points]);

  if (!points || pins.length === 0) {
    return (
      <div
        data-vaul-no-drag
        className="relative h-80 w-full shrink-0 overflow-hidden rounded-none bg-withus-dark-blue/5 sm:h-[420px]"
      >
        <div className="absolute inset-0 flex items-center justify-center text-sm text-withus-dark-blue/60">
          Locating properties…
        </div>
      </div>
    );
  }

  return (
    <div data-vaul-no-drag>
      <Map pins={pins} className="h-80 shrink-0 sm:h-[420px]" />
    </div>
  );
}
